import { apiClient, apiFetch, bearer, unwrap } from './client';
import type { components } from './types';

export type SourceDocumentKind = 'transactions' | 'suppliers' | 'normalized';

/** What the practitioner can upload. `normalized` is engine output, not
 *  practitioner input — produced by the engine after a run, never via
 *  the file pool endpoint. */
export type UploadableKind = 'transactions' | 'suppliers';

export type EngagementFileOut = components['schemas']['EngagementFileOut'];
export type EngagementFileListOut = components['schemas']['EngagementFileListOut'];

export function listEngagementFiles(
  engagementId: string,
  idToken: string,
): Promise<EngagementFileListOut> {
  return apiClient
    .GET('/api/engagements/{engagement_id}/files', {
      params: { path: { engagement_id: engagementId } },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementFileListOut>);
}

/** Files the customer uploaded through the portal, tagged with their
 *  onboarding category. Distinct from the practitioner pool above. */
export function listEngagementCustomerFiles(
  engagementId: string,
  idToken: string,
): Promise<EngagementFileListOut> {
  return apiClient
    .GET('/api/engagements/{engagement_id}/customer-files', {
      params: { path: { engagement_id: engagementId } },
      headers: bearer(idToken),
    })
    .then(unwrap<EngagementFileListOut>);
}

/** Multipart upload — on the legacy `apiFetch` path (openapi-fetch
 *  doesn't compose with `FormData` ergonomically). The server enforces
 *  the same contract; the type-safety loss here is bounded to file
 *  uploads. */
export async function uploadEngagementFile(
  engagementId: string,
  file: File,
  kind: UploadableKind,
  idToken: string,
): Promise<EngagementFileOut> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<EngagementFileOut>(
    `/api/engagements/${encodeURIComponent(engagementId)}/files?kind=${encodeURIComponent(kind)}`,
    {
      idToken,
      method: 'POST',
      body: form,
    },
  );
}

export async function deleteEngagementFile(
  engagementId: string,
  fileId: string,
  idToken: string,
): Promise<void> {
  await apiClient
    .DELETE('/api/engagements/{engagement_id}/files/{file_id}', {
      params: { path: { engagement_id: engagementId, file_id: fileId } },
      headers: bearer(idToken),
    })
    .then(unwrap<void>);
}
